// src/dnd/views/Sidebar_parts/LightingControls.js
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function clamp01(v) {
  return clamp(Number(v) || 0, 0, 1);
}

function sectionCardStyle() {
  return {
    background: "#242632",
    borderRadius: 8,
    padding: 10,
  };
}

function smallValueStyle() {
  return {
    fontSize: 12,
    color: "#9aa3b2",
  };
}

function SunDial({
  azimuth = 180,
  elevation = 45,
  onChange = () => {},
  size = 170,
  disabled = false,
}) {
  const ref = useRef(null);

  const cx = size / 2;
  const cy = size / 2;
  const rTrack = size / 2 - 12;
  const rKnob = 7;

  const mirrorAzimuth = (deg) => {
    const d = ((deg % 360) + 360) % 360;
    return (360 - d) % 360;
  };

  const uiAzimuth = mirrorAzimuth(azimuth);
  const elevClamped = clamp(elevation, 0, 90);
  const rNorm = 1 - elevClamped / 90;
  const angleRad = (uiAzimuth - 90) * (Math.PI / 180);
  const kx = cx + rNorm * rTrack * Math.cos(angleRad);
  const ky = cy + rNorm * rTrack * Math.sin(angleRad);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let dragging = false;

    const getPos = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const dx = x - cx;
      const dy = y - cy;
      const ang = Math.atan2(dy, dx);
      let uiDeg = (ang * 180) / Math.PI + 90;
      uiDeg = ((uiDeg % 360) + 360) % 360;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const r = clamp01(dist / rTrack);
      const elev = clamp((1 - r) * 90, 0, 90);
      return {
        azimuth: mirrorAzimuth(uiDeg),
        elevation: elev,
      };
    };

    const move = (e) => {
      if (!dragging) return;
      onChange(getPos(e));
    };

    const up = () => {
      dragging = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };

    const down = (e) => {
      if (disabled) return;
      dragging = true;
      onChange(getPos(e));
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    };

    el.addEventListener("pointerdown", down);

    return () => {
      el.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [cx, cy, disabled, onChange, rTrack]);

  const ring = (radius, color = "#2b2e39", w = 2, dash = "") => (
    <circle
      key={`r${radius}`}
      cx={cx}
      cy={cy}
      r={radius}
      fill="none"
      stroke={color}
      strokeWidth={w}
      strokeDasharray={dash}
      opacity={0.8}
    />
  );

  const tick = (deg, len = 7, w = 2, opacity = 0.5) => {
    const a = (deg - 90) * (Math.PI / 180);
    const x1 = cx + (rTrack - len) * Math.cos(a);
    const y1 = cy + (rTrack - len) * Math.sin(a);
    const x2 = cx + (rTrack + 2) * Math.cos(a);
    const y2 = cy + (rTrack + 2) * Math.sin(a);
    return (
      <line
        key={`t${deg}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#9aa3b2"
        strokeWidth={w}
        opacity={opacity}
      />
    );
  };

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      style={{ display: "block", touchAction: "none", opacity: disabled ? 0.5 : 1 }}
    >
      <circle cx={cx} cy={cy} r={rTrack} fill="#181a20" stroke="#2b2e39" strokeWidth="2" />
      {tick(0, 10, 2.5, 0.9)}
      {tick(90, 10, 2.5, 0.9)}
      {tick(180, 10, 2.5, 0.9)}
      {tick(270, 10, 2.5, 0.9)}
      {[...Array(12)].map((_, i) => tick(i * 30, 6, 1.5, 0.45))}
      {ring(rTrack * 1, "#2b2e39", 2)}
      {ring(rTrack * 0.66, "#2b2e39", 1.5, "2,4")}
      {ring(rTrack * 0.33, "#2b2e39", 1.5, "2,4")}
      <circle cx={cx} cy={cy} r={2.8} fill="#9aa3b2" />
      <circle cx={kx} cy={ky} r={rKnob + 2} fill="#0b0c10" stroke="#1f6feb" strokeWidth="2.5" />
      <circle cx={kx} cy={ky} r={rKnob} fill="#57a6ff" />
    </svg>
  );
}

export default function LightingControls({
  styles = {},
  mapId: mapIdProp = null,
  user,
  globalLightOpacity = null,
  setGlobalLightOpacity = () => {},
  onSaveGlobalLight = null,
  onSaveLighting = null,
}) {
  const params = useParams();
  const routeIdRaw = params?.id ?? params?.mapId ?? params?.map_id;
  const routeId = routeIdRaw != null && !Number.isNaN(Number(routeIdRaw)) ? Number(routeIdRaw) : null;
  const mapId = mapIdProp ?? routeId ?? null;
  const noMapId = mapId == null;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("globalLightingCollapsed");
      return v ? v === "1" : true;
    } catch {
      return true;
    }
  });

  const [celestialLoading, setCelestialLoading] = useState(false);
  const [lightIntensity, setLightIntensity] = useState(0.9);
  const [ambientDarkness, setAmbientDarkness] = useState(
    typeof globalLightOpacity === "number" ? clamp01(globalLightOpacity) : 0
  );
  const [shadowIntensity, setShadowIntensity] = useState(0.6);
  const [celestialColour, setCelestialColour] = useState("#ffe072");
  const [celestialAzimuth, setCelestialAzimuth] = useState(135);
  const [celestialElevation, setCelestialElevation] = useState(25);
  const [celestialSoftness, setCelestialSoftness] = useState(0.5);
  const [celestialReach, setCelestialReach] = useState(3000);

  const [globalLightTokenId, setGlobalLightTokenId] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  const darknessSaveTimerRef = useRef(null);
  const lightingSaveTimerRef = useRef(null);

  const putGlobalLight = async (val) => {
    if (noMapId) return;
    await fetch(`/api/tabletop/maps/${mapId}/global-light`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ global_light: val }),
    });
  };

  const putLighting = async (payload) => {
    if (noMapId) return;
    await fetch(`/api/tabletop/maps/${mapId}/lighting`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
  };

  const saveGlobalLight = typeof onSaveGlobalLight === "function" ? onSaveGlobalLight : putGlobalLight;
  const saveLighting = typeof onSaveLighting === "function" ? onSaveLighting : putLighting;

  const buildGlobalLightPayload = () => ({
    map_id: mapId,
    user_id: user?.id ?? null,
    label: "",
    icon_url: "",
    type: "global_light",
    x: 0,
    y: 0,
    intensity: lightIntensity,
    reach: celestialReach,
    colour: celestialColour,
    emits_light: 1,
    flicker: 0,
    visible_to_all: 1,
    azimuth_deg: celestialAzimuth,
    elevation_deg: celestialElevation,
    softness: celestialSoftness,
    enabled: 1,
  });

  useEffect(() => {
    setGlobalLightTokenId(null);
    setHydrated(false);
  }, [mapId]);

  useEffect(() => {
    if (typeof globalLightOpacity === "number") {
      setAmbientDarkness(clamp01(globalLightOpacity));
    }
  }, [globalLightOpacity]);

  useEffect(() => {
    if (noMapId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const r = await fetch(`/api/tabletop/maps/${mapId}/lighting`, { credentials: "include" });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;

        if (typeof j.global_light === "number") {
          setAmbientDarkness(clamp01(j.global_light));
        }
        if (typeof j.shadow_intensity === "number") {
          setShadowIntensity(clamp01(j.shadow_intensity));
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, [mapId, noMapId]);

  useEffect(() => {
    if (noMapId) return undefined;
    let cancelled = false;

    (async () => {
      setCelestialLoading(true);
      try {
        const res = await fetch(`/api/tabletop/tokens/${mapId}`, { credentials: "include" });
        if (!res.ok) return;
        const list = await res.json();
        if (cancelled) return;

        const gl = Array.isArray(list)
          ? list.find((t) => t && t.type === "global_light" && String(t.map_id) === String(mapId))
          : null;

        if (gl) {
          setGlobalLightTokenId(gl.id);
          setLightIntensity(typeof gl.intensity === "number" ? clamp01(gl.intensity) : 0.9);
          setCelestialColour(gl.colour || "#ffe072");
          setCelestialAzimuth(typeof gl.azimuth_deg === "number" ? gl.azimuth_deg : 135);
          setCelestialElevation(typeof gl.elevation_deg === "number" ? gl.elevation_deg : 25);
          setCelestialSoftness(typeof gl.softness === "number" ? clamp01(gl.softness) : 0.5);
          setCelestialReach(typeof gl.reach === "number" ? gl.reach : 3000);
          if (typeof gl.shadow_intensity === "number") {
            setShadowIntensity(clamp01(gl.shadow_intensity));
          }
        } else {
          const payload = buildGlobalLightPayload();
          const createRes = await fetch(`/api/tabletop/tokens`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          });
          if (createRes.ok) {
            const created = await createRes.json();
            setGlobalLightTokenId(created?.id ?? null);
          }
        }
      } catch {} finally {
        if (!cancelled) {
          setCelestialLoading(false);
          setHydrated(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mapId, noMapId]);

  useEffect(() => {
    try {
      localStorage.setItem("globalLightingCollapsed", collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    if (noMapId) return;

    const detail = {
      map_id: mapId,
      enabled: true,
      intensity: lightIntensity,
      ambient_darkness: ambientDarkness,
      shadow_intensity: shadowIntensity,
      colour: celestialColour,
      azimuth_deg: celestialAzimuth,
      elevation_deg: celestialElevation,
      softness: celestialSoftness,
      reach: celestialReach,
    };

    window.dispatchEvent(new CustomEvent("globalLight:update", { detail }));
    window.dispatchEvent(new CustomEvent(`globalLight:update:${mapId}`, { detail }));
  }, [
    ambientDarkness,
    celestialAzimuth,
    celestialColour,
    celestialElevation,
    celestialReach,
    celestialSoftness,
    lightIntensity,
    mapId,
    noMapId,
    shadowIntensity,
  ]);

  useEffect(() => {
    if (noMapId || !hydrated) return undefined;

    const payload = buildGlobalLightPayload();
    const id = globalLightTokenId;

    const timer = setTimeout(async () => {
      try {
        if (id) {
          await fetch(`/api/tabletop/tokens/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          });
        }
      } catch {}
    }, 220);

    return () => clearTimeout(timer);
  }, [
    celestialAzimuth,
    celestialColour,
    celestialElevation,
    celestialReach,
    celestialSoftness,
    globalLightTokenId,
    hydrated,
    lightIntensity,
    noMapId,
  ]);

  useEffect(() => {
    if (noMapId) return undefined;
    if (darknessSaveTimerRef.current) clearTimeout(darknessSaveTimerRef.current);

    darknessSaveTimerRef.current = setTimeout(() => {
      setGlobalLightOpacity(ambientDarkness);
      saveGlobalLight(ambientDarkness);
    }, 200);

    return () => {
      if (darknessSaveTimerRef.current) {
        clearTimeout(darknessSaveTimerRef.current);
        darknessSaveTimerRef.current = null;
      }
    };
  }, [ambientDarkness, noMapId, saveGlobalLight, setGlobalLightOpacity]);

  useEffect(() => {
    if (noMapId) return undefined;
    if (lightingSaveTimerRef.current) clearTimeout(lightingSaveTimerRef.current);

    lightingSaveTimerRef.current = setTimeout(() => {
      saveLighting({
        shadow_intensity: shadowIntensity,
      });
    }, 220);

    return () => {
      if (lightingSaveTimerRef.current) {
        clearTimeout(lightingSaveTimerRef.current);
        lightingSaveTimerRef.current = null;
      }
    };
  }, [mapId, noMapId, saveLighting, shadowIntensity]);

  if (noMapId) {
    return (
      <div
        style={{
          margin: "8px 12px 14px 12px",
          borderRadius: 10,
          background: "#1f2027",
          color: "#fff",
          padding: "10px 12px",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Global Lighting</div>
        <div style={{ color: "#cbd1dd", fontSize: 13 }}>
          No <code>maps.id</code> provided. Pass <code>mapId</code> or ensure the URL contains an <code>:id</code> param to enable lighting controls.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        margin: "8px 12px 14px 12px",
        borderRadius: 10,
        background: "#1f2027",
        color: "#fff",
        ...styles,
      }}
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        style={{
          width: "100%",
          textAlign: "left",
          background: "transparent",
          color: "#fff",
          border: "none",
          padding: "10px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontWeight: 800,
          fontSize: 16,
        }}
      >
        <span
          style={{
            display: "inline-block",
            transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
            transition: "transform 0.15s",
          }}
        >
          ▸
        </span>
        Global Lighting
      </button>

      <div
        style={{
          overflow: "hidden",
          maxHeight: collapsed ? 0 : 2200,
          transition: "max-height 220ms ease",
          padding: collapsed ? "0 12px" : "0 12px 12px 12px",
        }}
      >
        <div style={sectionCardStyle()}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 700 }}>Directional Light</div>
            <div style={{ fontSize: 12, color: "#bbb" }}>
              {celestialLoading ? "Loading…" : globalLightTokenId ? `Token #${globalLightTokenId}` : "Map setting"}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, alignItems: "start" }}>
            <div style={{ justifySelf: "center" }}>
              <SunDial
                azimuth={celestialAzimuth}
                elevation={celestialElevation}
                onChange={({ azimuth, elevation }) => {
                  setCelestialAzimuth(azimuth);
                  setCelestialElevation(elevation);
                }}
              />
              <div style={{ textAlign: "center", marginTop: 6, fontSize: 12, color: "#cfd6e4" }}>
                Direction: {Math.round(celestialAzimuth)}° • Angle: {Math.round(celestialElevation)}°
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, marginBottom: 4 }}>Ambient Darkness: {ambientDarkness.toFixed(2)}</div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={ambientDarkness}
                onChange={(e) => setAmbientDarkness(clamp01(e.target.value))}
                style={{ width: "100%", marginBottom: 10 }}
              />

              <div style={{ fontSize: 13, marginBottom: 4 }}>Shadow Intensity: {shadowIntensity.toFixed(2)}</div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={shadowIntensity}
                onChange={(e) => setShadowIntensity(clamp01(e.target.value))}
                style={{ width: "100%", marginBottom: 10 }}
              />

              <div style={{ fontSize: 13, marginBottom: 4 }}>Light Intensity: {lightIntensity.toFixed(2)}</div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={lightIntensity}
                onChange={(e) => setLightIntensity(clamp01(e.target.value))}
                style={{ width: "100%", marginBottom: 10 }}
              />

              <div style={{ fontSize: 13, marginBottom: 4 }}>Direction: {Math.round(celestialAzimuth)}°</div>
              <input
                type="range"
                min={0}
                max={359}
                step={1}
                value={Math.round(celestialAzimuth)}
                onChange={(e) => setCelestialAzimuth(Number(e.target.value))}
                style={{ width: "100%", marginBottom: 10 }}
              />

              <div style={{ fontSize: 13, marginBottom: 4 }}>Angle: {Math.round(celestialElevation)}°</div>
              <input
                type="range"
                min={0}
                max={90}
                step={1}
                value={Math.round(celestialElevation)}
                onChange={(e) => setCelestialElevation(Number(e.target.value))}
                style={{ width: "100%", marginBottom: 10 }}
              />

              <div style={{ fontSize: 13, marginBottom: 4 }}>Soft Shadow: {celestialSoftness.toFixed(2)}</div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={celestialSoftness}
                onChange={(e) => setCelestialSoftness(clamp01(e.target.value))}
                style={{ width: "100%", marginBottom: 10 }}
              />

              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, marginBottom: 8 }}>
                Colour
                <input
                  type="color"
                  value={celestialColour}
                  onChange={(e) => setCelestialColour(e.target.value)}
                  style={{
                    width: 44,
                    height: 28,
                    border: "1px solid #3a3c49",
                    background: "#1b1c23",
                    borderRadius: 6,
                  }}
                />
                <span style={smallValueStyle()}>{celestialColour}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}